/* eslint-disable react/jsx-handler-names */
"use client";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Drawer from "../drawer";
import Portal from "../Portal";
import { InfoIcon } from "@phosphor-icons/react";
import { formatClassNames } from "../../../utils/common";

/**
 * Information Button Component that displays a button.
 * @returns {JSX.Element} - Rendered component.
 */
const InformationButton = ({
  renderInformation,
  isMobile,
  alignment = "left",
  rootId = "mobileLocationLock",
  iconClasses = "",
}) => {
  const buttonRef = useRef(null);
  const [informationVisible, setInformationVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const updateTooltipPosition = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    setTooltipPosition({
      top: rect.bottom + 10,
      left: alignment === "right" ? rect.right - 300 + 24 : rect.left - 24,
    });
  }, [alignment]);

  const handleMouseLeave = useCallback(() => setInformationVisible(false), []);

  const handleMouseEnter = useCallback(() => {
    updateTooltipPosition();
    setInformationVisible(true);
  }, [updateTooltipPosition]);

  useEffect(() => {
    const button = buttonRef.current;

    if (button && !isMobile) {
      button.addEventListener("mouseleave", handleMouseLeave);
      button.addEventListener("mouseenter", handleMouseEnter);
    }

    return () => {
      if (button && !isMobile) {
        button.removeEventListener("mouseleave", handleMouseLeave);
        button.removeEventListener("mouseenter", handleMouseEnter);
      }
    };
  }, [handleMouseEnter, handleMouseLeave, isMobile]);

  /**
   * Toggles the visibility of the information section.
   * @returns {void}
   */
  const onHandleVisibility = () => {
    setInformationVisible((isVisible) => {
      const nextVisible = !isVisible;
      if (nextVisible) {
        updateTooltipPosition();
      }
      return nextVisible;
    });
  };

  /**
   * Renders desktop tooltip in a body portal so it is not clipped by overflow parents.
   * @returns {import("react").ReactPortal|null}
   */
  const renderDesktopTooltip = () => {
    if (!informationVisible || isMobile || typeof document === "undefined") {
      return null;
    }

    return createPortal(
      <div
        role="tooltip"
        className="fixed z-10 w-fit max-w-[310px] rounded-2xl bg-white-900 p-16 shadow-[0_0px_6px_rgba(0,0,0,0.2)]"
        style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
      >
        <div
          className={formatClassNames(
            "absolute -top-10 h-20 w-20 rotate-45 bg-white-900 shadow-[-4px_-3px_4px_rgba(0,0,0,0.1)]",
            alignment === "right" ? "right-32" : "left-32",
          )}
        />
        {renderInformation !== undefined && renderInformation}
      </div>,
      document.body,
    );
  };

  return (
    <>
      {isMobile ? (
        <Portal rootId={rootId} renderOnDidMount={true}>
          <Drawer position="bottom" isOpen={informationVisible} onDrawerClose={() => setInformationVisible(false)}>
            {renderInformation}
          </Drawer>
        </Portal>
      ) : (
        renderDesktopTooltip()
      )}
      <div className={formatClassNames("relative inline-flex", !isMobile && informationVisible && "z-10")}>
        <button
          onClick={(e) => {
            e.preventDefault();
            isMobile ? setInformationVisible(true) : onHandleVisibility();
          }}
          ref={buttonRef}
          aria-label="info"
          className={formatClassNames(
            "relative top-[2.5px] flex h-5 w-5 items-center justify-center rounded-full focus:outline-none md:top-0",
            iconClasses,
          )}
        >
          <InfoIcon size={16} color="#757575" weight="bold" />
        </button>
      </div>
    </>
  );
};

InformationButton.propTypes = {
  renderInformation: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  alignment: PropTypes.string,
  rootId: PropTypes.string,
  iconClasses: PropTypes.string,
};

export default InformationButton;
